import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { PageHeader } from "@/components/shared";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";

const TIER_LABEL = {
  senior: "Senior R.A.",
  junior: "Junior R.A.",
  new_joiner: "New Joiner / Fresher",
};
const METRIC_LABEL = {
  leads_per_day: "Leads / day",
  positive_clients_per_week: "Positive clients / week",
  closers_per_quarter: "Closers / quarter",
  placements_per_quarter: "Placements / quarter",
};

function Bar({ label, value, target }) {
  const pct = target ? Math.min(100, Math.round((value / target) * 100)) : 0;
  return (
    <div data-testid={`ra-target-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <div className="flex justify-between text-sm mb-1"><span>{label}</span><span className="font-medium">{value} / {target || "—"}</span></div>
      <Progress value={pct} className="h-2" />
    </div>
  );
}

export default function RATargets() {
  const [d, setD] = useState(null);

  useEffect(() => {
    Promise.all([api.get("/ra/targets"), api.get("/ra/performance")])
      .then(([t, p]) => setD({ target: t.data, perf: p.data.performance }))
      .catch((e) => toast.error(apiErr(e)));
  }, []);

  if (!d) return <div className="space-y-4"><Skeleton className="h-24" /><Skeleton className="h-48" /></div>;
  const eff = d.target?.effective || {};
  const tier = d.target?.tier || "junior";
  const p = d.perf;

  return (
    <div>
      <PageHeader title="My Targets" subtitle="Your tier goals for this period" />
      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="p-6 space-y-4" data-testid="ra-tier-plan-card">
          <div className="flex items-center justify-between">
            <div className="text-xs text-muted-foreground uppercase tracking-wide">Target tier</div>
            <span className="rounded-full bg-[#255058] px-3 py-1 text-xs font-medium text-white" data-testid="ra-tier-badge">
              {TIER_LABEL[tier] || tier}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {Object.keys(METRIC_LABEL).map((m) => (
              <div key={m} className="border-l-2 border-[#255058] pl-3" data-testid={`ra-tier-metric-${m}`}>
                <div className="font-display text-2xl font-bold">{eff[m] ?? "—"}</div>
                <div className="text-xs text-muted-foreground">{METRIC_LABEL[m]}</div>
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">
            Targets are set by tier and can be adjusted by your R.A. Admin. Changes apply to future
            periods; past performance stays measured against the target active at that time.
          </p>
        </Card>
        <Card className="p-6 space-y-4" data-testid="ra-target-progress-card">
          <div className="text-xs text-muted-foreground uppercase tracking-wide">Activity this period</div>
          <Bar label="Companies Researched" value={p.total_leads} target={d.target?.target?.companies} />
          <Bar label="Valid Emails Found" value={p.valid_emails} target={d.target?.target?.valid_emails} />
          <Bar label="Positive Leads" value={p.positive_leads} target={eff.positive_clients_per_week} />
          <p className="text-xs text-muted-foreground">Duplicate/restricted leads are excluded from these counts.</p>
        </Card>
      </div>
    </div>
  );
}
